import { GoldButton } from '@components/button/Button'
import ElementShowcase from '@components/element/ElementShowcase'
import Column from '@components/layout/Column'
import Row from '@components/layout/Row'
import Evolutions from '@components/sentius/Evolutions'
import SentiusInformationBox from '@components/sentius/SentiusInformationBox'
import IntroductionBox from '@components/sentius/SentiusIntroduction'
import SentiusShowcase from '@components/sentius/SentiusShowcase'
import LeftRightBg from '@components/shapes/LeftRightBg'
import Separator from '@components/shapes/Separator'
import StatsProvider from '@components/stats/StatsProvider'
import InlineTitle from '@components/title/InlineTitle'
import TrapezoidTitle from '@components/title/TrapezoidTitle'
import useMediaQuery, { maxWidthQueries } from '@operations/utils/useMediaQuery'
import { useRouter } from 'next/router'
import React from 'react'
import styled, { useTheme } from 'styled-components'

const CapitalizeSpan = styled.span`
  text-transform: capitalize;
`

const defaultStats = {
  attack: 0,
  defense: 0,
  speed: 0,
}

const Sentius = ({ sentius, evolutions }) => {
  const theme = useTheme()
  const router = useRouter()
  const isMobile = useMediaQuery(maxWidthQueries.sm)
  const sexesLength = sentius.sexes.length

  const imagesArr = []
  if (sentius.frontImage) {
    imagesArr.push({
      alt: sentius.frontImage?.alternativeText,
      medium: sentius.frontImage?.formats?.medium?.url,
      small: sentius.frontImage?.formats?.small?.url,
      thumbnail: sentius.frontImage?.formats?.thumbnail?.url,
    })
  }
  if (sentius.sideImage) {
    imagesArr.push({
      alt: sentius.sideImage?.alternativeText,
      medium: sentius.sideImage?.formats?.medium?.url,
      small: sentius.sideImage?.formats?.small?.url,
      thumbnail: sentius.sideImage?.formats?.thumbnail?.url,
    })
  }

  return (
    <>
      <LeftRightBg />
      <TrapezoidTitle title={sentius.name} />
      <Row align="flex-start" margin="0 0 1rem">
        {sentius.shortDescription && <IntroductionBox>{sentius.shortDescription}</IntroductionBox>}
        <Column align="flex-start" margin="0 0 1rem" width={isMobile ? 'auto' : '50%'}>
          <SentiusShowcase
            id={sentius.id}
            images={
              sentius.media?.length > 0
                ? sentius.media?.map(item => ({
                    alt: item.alternativeText,
                    medium: item?.formats?.medium?.url,
                    small: item?.formats?.small?.url,
                    thumbnail: item?.formats?.thumbnail?.url,
                  }))
                : imagesArr
            }
          />
        </Column>
        <Column width={isMobile ? '100%' : '45%'}>
          {sentius.element?.length > 0 && (
            <Column justify={isMobile ? 'flex-start' : 'flex-end'} margin="0 0 1rem">
              <InlineTitle title="Types" />
              <ElementShowcase
                elements={sentius.element}
                justify={isMobile ? 'flex-start' : 'flex-end'}
              />
            </Column>
          )}
          <Separator />
          {sentius.weakness?.length > 0 && (
            <Column justify={isMobile ? 'flex-start' : 'flex-end'} margin="0 0 1rem">
              <InlineTitle title="Weakness" />
              <ElementShowcase
                elements={sentius.weakness}
                justify={isMobile ? 'flex-start' : 'flex-end'}
              />
            </Column>
          )}
        </Column>
      </Row>

      <Row align="stretch" margin={isMobile ? '0 0 1rem' : '0 0 2rem'} position="relative">
        <Column
          background={theme.colors.black}
          color={theme.colors.white}
          justify="center"
          margin={isMobile ? '0 0 1rem' : '0 1rem 0 0'}
          width={isMobile ? '100%' : 'calc(40% - 1rem)'}
        >
          <StatsProvider stats={sentius.stats || defaultStats} />
        </Column>
        <Column
          background={theme.colors.black}
          color={theme.colors.white}
          justify="center"
          margin={isMobile ? '0 0 1rem' : '0 1rem 0 0'}
          width={isMobile ? '100%' : 'calc(40% - 1rem)'}
        >
          <Row
            background={{ backgroundColor: theme.colors.black }}
            color={theme.colors.white}
            padding="1rem"
          >
            <Column margin="0 0 1rem" width="50%">
              <CapitalizeSpan>Nature: {sentius.nature || 'unknown'}</CapitalizeSpan>
            </Column>
            <Column margin="0 0 1rem" width="50%">
              <CapitalizeSpan>
                Sex:{' '}
                {sentius.sexes && sexesLength
                  ? sentius.sexes.map(({ id, sex }, index) => (
                      <React.Fragment key={id}>
                        {sex}
                        {sexesLength - 1 !== index && '/'}
                      </React.Fragment>
                    ))
                  : 'unknown'}
              </CapitalizeSpan>
            </Column>

            <Column width="50%">
              <CapitalizeSpan>Height: {sentius.height || 'unknown'}</CapitalizeSpan>
            </Column>
            <Column width="50%">
              <CapitalizeSpan>Weight: {sentius.weight || 'unknown'}</CapitalizeSpan>
            </Column>
          </Row>
        </Column>
        <Column direction="column" justify="center" width={isMobile ? '100%' : '20%'}>
          Size
          {sentius.sizeReference ? (
            <img
              alt={`sentius size = ${sentius.sizeReference}`}
              src={`/images/${sentius.sizeReference}_sentius.png`}
              style={{
                maxWidth: '100%',
              }}
            />
          ) : (
            ': Unknown'
          )}
        </Column>
      </Row>

      {sentius.description && <SentiusInformationBox>{sentius.description}</SentiusInformationBox>}

      {evolutions?.evolution?.length > 0 && <Evolutions evolutions={evolutions.evolution} />}
      <Row justify="center" margin="2rem 0 1rem">
        <GoldButton
          label="Explore more Sentius"
          type="button"
          onClick={() =>
            router.push(
              `/sentipedia${
                router.query?.dynastyFilter ? `?dynasty=${router.query?.dynastyFilter}` : ''
              }`,
              undefined,
              {
                shallow: true,
              }
            )
          }
        />
      </Row>
    </>
  )
}

export default Sentius
